import { Autocomplete, Grid, TextField, Button, Typography, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DatePicker } from '@mui/x-date-pickers';
import { FormikProps } from 'formik';
import RowOfStatement from './RowOfStatement';
import dayjs from 'dayjs';
import { FormValues } from '../FormPagePerson';
import { useLanguage } from '../i18n/LanguageContext';
import { TranslationKey } from '../i18n/translations';

// Wartość stopnia niepełnosprawności zostaje po polsku (tak oczekuje backend),
// tłumaczona jest tylko etykieta w liście.
const DEGREE_OPTIONS: { value: string; key: TranslationKey }[] = [
    { value: 'znaczny', key: 'degree.znaczny' },
    { value: 'umiarkowany', key: 'degree.umiarkowany' },
    { value: 'lekki', key: 'degree.lekki' },
];

const Statements = ({ formik }: { formik: FormikProps<FormValues> }) => {

    const { values, handleBlur, errors, touched, setFieldValue } = formik;
    const { t } = useLanguage();


    return (
        <>
            {/* umowa o prace */}
            <RowOfStatement
                formik={formik}
                title={t('st.socialInsurance')}
                name="socialInsuranceFromOtherContract"
                additionalInput={
                    formik.values.socialInsuranceFromOtherContract === true && (
                        <TextField
                            fullWidth
                            sx={{ maxWidth: 600 }}
                            label={t('st.entityNameAddress')}
                            name="entityName"
                            value={formik.values.entityName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.entityName && Boolean(formik.errors.entityName)}
                            helperText={formik.touched.entityName && formik.errors.entityName}
                        />
                    )
                }
            />
            <RowOfStatement
                show={formik.values.socialInsuranceFromOtherContract === true}
                formik={formik}
                title={t('st.salaryAboveMin')}
                name="salaryAboveMinimum"
            />
            {/* umowa zlecenie */}
            <RowOfStatement
                formik={formik}
                title={t('st.mandate')}
                name="contractOfMandateWithOtherEntity"
                additionalInput={
                    formik.values.contractOfMandateWithOtherEntity === true && (
                        <TextField
                            fullWidth
                            sx={{ maxWidth: 600 }}
                            label={t('st.entityNameAddress')}
                            name="nameAndAddress"
                            value={formik.values.nameAndAddress}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.nameAndAddress && Boolean(formik.errors.nameAndAddress)}
                            helperText={formik.touched.nameAndAddress && formik.errors.nameAndAddress}
                        />
                    )
                }
            />
            <RowOfStatement
                show={formik.values.contractOfMandateWithOtherEntity === true}
                formik={formik}
                title={t('st.mandateSalaryAboveMin')}
                name="mandate_salaryAboveMinimum"
            />
            <RowOfStatement
                show={formik.values.contractOfMandateWithOtherEntity === true}
                formik={formik}
                title={t('st.mandatePeriod')}
                name="mandate_salaryAboveMinimum"
                input={<Grid container sx={{ justifyContent: 'space-between' }}>
                    <Grid size={5.25}>
                        <DatePicker
                            label={t('st.from')}
                            defaultValue={values.periodFrom ? dayjs(values.periodFrom) : null}
                            onChange={(newValue) => {
                                setFieldValue('periodFrom', newValue ? newValue.toDate() : null);
                            }}
                            slotProps={{
                                textField: {
                                    name: "periodFrom",
                                    onBlur: handleBlur,
                                    error: touched.periodFrom && Boolean(errors.periodFrom),
                                    helperText: touched.periodFrom && errors.periodFrom,
                                    fullWidth: true,
                                }
                            }}
                        />
                    </Grid>
                    <Grid
                        size={0.5}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        -
                    </Grid>
                    <Grid size={5.25}>
                        <DatePicker
                            label={t('st.to')}
                            defaultValue={values.periodTo ? dayjs(values.periodTo) : null}
                            minDate={values.periodFrom ? dayjs(values.periodFrom) : undefined}
                            onChange={(newValue) => {
                                setFieldValue('periodTo', newValue ? newValue.toDate() : null);
                            }}
                            slotProps={{
                                textField: {
                                    name: "periodTo",
                                    onBlur: handleBlur,
                                    error: touched.periodTo && Boolean(errors.periodTo),
                                    helperText: touched.periodTo && errors.periodTo,
                                    fullWidth: true,
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                }
            />

            <RowOfStatement
                formik={formik}
                title={t('st.retired')}
                name="retired"
            />
            <RowOfStatement
                formik={formik}
                title={t('st.pensioner')}
                name="pensioner"
            />
            <RowOfStatement
                formik={formik}
                title={t('st.student')}
                name="student"
                additionalInput={
                    values.student === true && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                            <TextField
                                sx={{ maxWidth: 300 }}
                                fullWidth
                                label={t('st.studentIdNumber')}
                                name="studentIdNumber"
                                value={values.studentIdNumber}
                                onChange={formik.handleChange}
                                onBlur={handleBlur}
                                error={touched.studentIdNumber && Boolean(errors.studentIdNumber)}
                                helperText={touched.studentIdNumber && errors.studentIdNumber}
                            />
                            <Box sx={{ maxWidth: 300, width: '100%' }}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    fullWidth
                                >
                                    {values.studentIdScan
                                        ? (values.studentIdScan as File).name
                                        : t('st.uploadStudentId')}
                                    <input
                                        type="file"
                                        hidden
                                        accept="application/pdf,image/jpeg,image/png"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] || null;
                                            setFieldValue('studentIdScan', file);
                                        }}
                                    />
                                </Button>
                                {touched.studentIdScan && errors.studentIdScan && (
                                    <Typography color="error" sx={{ fontSize: '12px', mt: 0.5 }}>
                                        {errors.studentIdScan as string}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )
                }
            />
            <RowOfStatement
                formik={formik}
                title={t('st.disability')}
                name="disability"
                additionalInput={
                    formik.values.disability === true && (
                        <Autocomplete

                            disablePortal
                            options={DEGREE_OPTIONS}
                            getOptionLabel={(option) => t(option.key)}
                            value={DEGREE_OPTIONS.find(opt => opt.value === values.degree) || null}
                            onChange={(_, newValue) => setFieldValue('degree', newValue?.value || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{ maxWidth: 300 }}

                                    label={t('st.disabilityDegree')}
                                    name="degree"
                                    onBlur={handleBlur}
                                    error={touched.degree && Boolean(errors.degree)}
                                    helperText={touched.degree && errors.degree}
                                />
                            )}
                            sx={{ width: '100%' }}
                        />
                    )
                }
            />
            <RowOfStatement
                formik={formik}
                title={t('st.voluntaryHealth')}
                name="voluntaryHealthInsurance"
            />
            <RowOfStatement
                formik={formik}
                title={t('st.business')}
                name="businessActivity"
            />
        </>
    );
};

export default Statements;
